import { format as formatDate, isValid } from 'date-fns'
import { parseIsoDate } from './date'
import { getExpectedDigitCount } from './helper-functions'

/**
 * Extracts the last format segment from a pattern to be repeated.
 * @param pattern - The format pattern (e.g., "XXX XXX XXX")
 * @returns The last segment (e.g., " XXX")
 */
export const getLastFormatSegment = (pattern: string): string => {
  if (!pattern) return ''

  const lastX = pattern.lastIndexOf('X')
  if (lastX === -1) {
    return '' // No digit placeholders
  }

  // Find the start of the last group of 'X's
  let startOfLastSegment = lastX
  while (startOfLastSegment > 0 && pattern[startOfLastSegment - 1] === 'X') {
    startOfLastSegment--
  }

  // Find the end of the second-to-last group of 'X's to find the separator
  let endOfPreviousSegment = -1
  for (let i = startOfLastSegment - 1; i >= 0; i--) {
    if (pattern[i] === 'X') {
      while (i > 0 && pattern[i - 1] === 'X') {
        i--
      }
      endOfPreviousSegment = i
      break
    }
  }

  if (endOfPreviousSegment !== -1) {
    // There was a segment before this one, so the segment includes the separator.
    let separatorStart = -1
    for (let i = startOfLastSegment - 1; i > endOfPreviousSegment; i--) {
      if (pattern[i] !== 'X') {
        separatorStart = i
        while (separatorStart > 0 && pattern[separatorStart - 1] !== 'X') {
          separatorStart--
        }
        break
      }
    }
    if (separatorStart !== -1) {
      return pattern.substring(separatorStart)
    }
  }

  // This is the only/first segment.
  return pattern.substring(startOfLastSegment)
}

/**
 * Creates an extended pattern by repeating the last segment for the given digit count.
 * @param originalPattern - The original format pattern
 * @param totalDigits - Total number of digits to accommodate
 * @returns Extended pattern
 */
export const createExtendedPattern = (originalPattern: string, totalDigits: number): string => {
  const originalDigitCount = getExpectedDigitCount(originalPattern)

  if (totalDigits <= originalDigitCount) {
    return originalPattern
  }

  const lastSegment = getLastFormatSegment(originalPattern)
  if (!lastSegment) return originalPattern
  const segmentDigitCount = getExpectedDigitCount(lastSegment)

  if (segmentDigitCount === 0) {
    return originalPattern
  }

  // Check if the original pattern ends with the last segment. If not, it means there's a suffix.
  if (!originalPattern.endsWith(lastSegment)) {
    return originalPattern
  }

  const additionalDigitsNeeded = totalDigits - originalDigitCount
  // Use Math.ceil to ensure we have enough space for all digits
  const additionalSegments = Math.ceil(additionalDigitsNeeded / segmentDigitCount)

  let extendedPattern = originalPattern
  for (let i = 0; i < additionalSegments; i++) {
    extendedPattern += lastSegment
  }

  return extendedPattern
}

/**
 * Validates if a string value matches a pattern structure, including extended patterns.
 * @param value - The value to validate.
 * @param pattern - The pattern to validate the value by.
 * @returns True if the value matches the pattern, false otherwise.
 */
export const isValidStringFormatStructure = (
  value: string,
  pattern: string,
  maxLength?: number,
): boolean => {
  if (!value) return true // Empty value is always valid

  const digitsInValue = (value.match(/\d/g) ?? []).length
  if (maxLength && digitsInValue > maxLength) {
    return false
  }
  if (!pattern) return true // Non-empty value with empty pattern is valid for raw input

  const extendedPattern = createExtendedPattern(pattern, digitsInValue)

  if (value.length > extendedPattern.length) return false

  for (let i = 0; i < value.length; i++) {
    const char = value[i]
    const patternChar = extendedPattern[i]

    if (patternChar === 'X') {
      // Pattern expects digit, input must be digit
      if (!/\d/.test(char)) return false
    } else {
      // Pattern expects separator, input must match separator or be digit (for auto-formatting)
      if (char !== patternChar && !/\d/.test(char)) return false
    }
  }

  return true
}

/**
 * Formats a string value based on a pattern, with support for repeating the last pattern segment.
 * @param value - The value to format.
 * @param pattern - The pattern to format the value by.
 * @returns The formatted value.
 */
export const formatStringValue = (value: string, pattern: string): string => {
  if (!value || !pattern) return value

  // Remove all non-digits
  const digitsOnly = value.replace(/\D/g, '')

  if (digitsOnly.length === 0) return ''

  // Create extended pattern to accommodate all digits
  const extendedPattern = createExtendedPattern(pattern, digitsOnly.length)

  let formatted = ''
  let digitIndex = 0

  for (let i = 0; i < extendedPattern.length && digitIndex < digitsOnly.length; i++) {
    if (extendedPattern[i] === 'X') {
      formatted += digitsOnly[digitIndex]
      digitIndex++
    } else {
      formatted += extendedPattern[i]
    }
  }

  return formatted
}

/**
 * Formats a Norwegian organization number into groups of three digits: "XXX XXX XXX".
 * Non-digit characters are stripped before formatting.
 */
export const formatOrganizationNumber = (value: string): string => {
  return formatStringValue(value, 'XXX XXX XXX')
}

export const formatMaskedNorwegianSsn = (
  ssn?: string | null,
  birthDate?: string | Date | null,
): string | null => {
  const digitsOnly = ssn?.replace(/\D/g, '') ?? ''

  if (digitsOnly.length >= 6) {
    return digitsOnly.slice(0, 6)
  }

  const parsedBirthDate =
    birthDate instanceof Date ? (isValid(birthDate) ? birthDate : null) : parseIsoDate(birthDate)

  if (!parsedBirthDate) {
    return null
  }

  return formatDate(parsedBirthDate, 'ddMMyy')
}

/**
 * Formats a number with a specified number of decimal places and space as thousand separator.
 * (e.g. formatNumber(1234.56, 0) → "1 235", formatNumber(1234.56, 1) → "1 234,6")
 */
export const formatNumber = (value: number, decimals: number = 0): string => {
  return (decimals === 0 ? Math.round(value) : value)
    .toLocaleString('nb-NO', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
    .replace(/\u00A0/g, ' ')
}

/**
 * Formats a number as currency with space as thousand separator.
 * By default shows decimals only when needed (e.g. 1000 → "1 000", 1000.50 → "1 000,50").
 * Pass `{ alwaysDecimals: true }` to always show two decimals (e.g. 1000 → "1 000,00").
 */
export const formatCurrency = (value: number, options?: { alwaysDecimals?: boolean }): string => {
  const normalized = value === 0 ? 0 : value
  const roundedValue = Math.round(normalized * 100) / 100
  const hasDecimals = roundedValue % 1 !== 0
  const minimumFractionDigits = options?.alwaysDecimals ? 2 : hasDecimals ? 2 : 0

  return normalized
    .toLocaleString('nb-NO', {
      minimumFractionDigits,
      maximumFractionDigits: 2,
    })
    .replace(/\u00A0/g, ' ')
}

/**
 * Formats a number with always two decimals and space as thousand separator.
 * Always shows decimals (e.g. 1000 → "1 000,00").
 * @deprecated Use `formatCapital` for capital/financial values or `formatNumber(value, 2)` for generic use.
 */
export const formatCurrencyWithDecimals = (value: number): string => formatNumber(value, 2)

/**
 * Formats a number with one decimal and space as thousand separator.
 * Always shows one decimal (e.g. 1000 -> "1 000,0").
 * @deprecated Use `formatArea` for area values, `formatShares` for share counts, or `formatNumber(value, 1)` for generic use.
 */
export const formatNumberWithOneDecimal = (value: number): string => formatNumber(value, 1)

/**
 * Formats an area value (decare) with one decimal and space as thousand separator.
 * (e.g. 1234.5 → "1 234,5")
 */
export const formatArea = (value: number): string => formatNumber(value, 1)

/**
 * Formats a share count with one decimal and space as thousand separator.
 * (e.g. 1234.5 → "1 234,5")
 */
export const formatShares = (value: number): string => formatNumber(value, 1)

/**
 * Formats a capital/financial value with always two fixed decimals and space as thousand separator.
 * (e.g. 1234.5 → "1 234,50", 1234 → "1 234,00")
 */
export const formatCapital = (value: number): string => formatNumber(value, 2)

/**
 * Formats final displayed monetary values for nb-NO.
 * Whole amounts use the historical ",-" suffix, while decimal amounts use two decimals and no suffix.
 * (e.g. 1234 -> "1 234,-", 1234.5 -> "1 234,50")
 */
export const formatAmount = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '0,-'
  }

  const normalized = value === 0 ? 0 : value
  const roundedValue = Math.round(normalized * 100) / 100
  const hasDecimals = roundedValue % 1 !== 0

  if (hasDecimals) {
    return formatCurrency(roundedValue, { alwaysDecimals: true })
  }

  return `${formatCurrency(roundedValue)},-`
}

/**
 * Formats a signed surcharge for display: positive values get an explicit "+" prefix, negative
 * values keep the locale minus sign. (e.g. 10 -> "+10,-", -20 -> "−20,-")
 */
export const formatSurchargeAmount = (value: number): string =>
  value > 0 ? `+${formatAmount(value)}` : formatAmount(value)

/**
 * Formats a percentage with up to two decimals, trimming trailing zeros.
 * (e.g. 50 → "50", 33.3 → "33,3", 33.33 → "33,33")
 */
export const formatPercentage = (value: number): string => {
  if (Number.isInteger(value)) return formatNumber(value, 0)
  return formatNumber(value, 2).replace(/,?0+$/, '')
}

export function padCustomerNumber(customerNumber: string | undefined): string {
  if (!customerNumber) {
    return ''
  }
  return customerNumber.padStart(10, '0')
}

/**
 * Formats a property HNR (Hovednummer) to display only the farmNumber/holdingNumber (first two parts).
 * Removes municipality number prefix and any lease/section numbers.
 * Examples:
 * - "3812-38/1/0-1" -> "38/1"
 * - "3812-38/1" -> "38/1"
 * - "38/1/0-1" -> "38/1"
 * - "38/1" -> "38/1"
 */
export function formatPropertyHnr(propertyNumber: string): string {
  if (!propertyNumber) {
    return ''
  }

  // Remove municipality number prefix if present (format: "municipalityNumber-farmNumber/holdingNumber/...")
  const afterDash = propertyNumber.includes('-') ? propertyNumber.split('-')[1] : propertyNumber

  // Extract just the first two parts (farmNumber/holdingNumber)
  // Split by '/' and take only the first two parts
  const parts = afterDash.split('/')
  return parts.slice(0, 2).join('/')
}

/**
 *
 * Parses a string value into a number, ensuring it has at most the specified number of decimal places.
 */
export const parseDecimalValue = (value: string, maximumDecimals: number): number | null => {
  const normalizedValue = value.trim().replace(',', '.')

  if (!/^\d+(?:\.\d*)?$/.test(normalizedValue)) {
    return null
  }

  const parsedValue = Number(normalizedValue)
  const roundedValue = Math.round(parsedValue * 10 ** maximumDecimals) / 10 ** maximumDecimals

  return Number.isFinite(roundedValue) ? roundedValue : null
}
